import { ReactNode, useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Shield } from "lucide-react";
import { adminAPI } from "@/lib/admin-api";
import { ApiError } from "@/lib/api";

interface AuthGuardProps {
  children: ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      // Fast-fail: if no local token or it is already expired, redirect immediately
      if (!adminAPI.isAuthenticated()) {
        setIsLoading(false);
        window.location.href = "/admin/login";
        return;
      }

      // Verify the token is still valid server-side (catches revoked sessions).
      // If the server is unreachable we fall back to trusting the local expiry
      // rather than locking the user out on a network blip.
      try {
        await adminAPI.verifySession();
        setIsAuthenticated(true);
      } catch (err) {
        // ApiError with 401/403 means the server actively rejected the token
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          localStorage.removeItem("adminToken");
          localStorage.removeItem("adminUser");
          localStorage.removeItem("adminTokenExpires");
          setIsLoading(false);
          window.location.href = "/admin/login";
          return;
        }
        // Network error or non-auth failure — allow access based on local token
        setIsAuthenticated(true);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await adminAPI.logout();
      window.location.href = "/admin/login";
    } catch (error) {
      console.error("Logout error:", error);
      localStorage.removeItem("adminToken");
      localStorage.removeItem("adminUser");
      localStorage.removeItem("adminTokenExpires");
      window.location.href = "/admin/login";
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-8 h-8 text-primary animate-pulse mx-auto mb-4" />
          <p className="text-muted-foreground">Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-8 h-8 text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
